import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface PeriodWeekData {
  message: string;
  data: {
    period: number;
    week: number;
    year: number;
  };
}

export interface EnrichedLocation {
  id: string;
  name: string;
  locationId: number | null;
  isLive: string;
  locSapId: string | null;
  size: string | null;
  locationType: string | null;
  locationFacilityType: string | null;
  organizationType: string | null;
  is3pl: boolean | null;
  region: string | null;
  market: string | null;
  status: string | null;
  isDuo: boolean | null;
  executionSystem: string | null;
}

interface LocationWithAccessResponse {
  message: string;
  data: {
    userInfo: {
      email: string;
      roleId: number;
      roleName: string;
      accessType: string;
      hasFullAccess: boolean;
      isLive: string;
      GPID?: string;
      sustainTeamMember: boolean;
    };
    locations: EnrichedLocation[];
    moduleAccess: Array<{
      accessId: number;
      moduleId: number;
      moduleName: string;
      hasAccess: boolean;
      readOnly: boolean;
    }>;
    summary: {
      totalLocations: number;
      totalModules: number;
      accessibleModules: number;
    };
  };
}

interface BasicAccessUser {
  userEmail: string;
  gpid: string;
  status: boolean;
  team: string;
  naviAccessAssignee?: string;
}

interface AppDataState {
  locations: EnrichedLocation[];
  selectedLocation: any | null;
  periodWeek: PeriodWeekData;
  roles: any[];
  userManagementRoles: Array<{ roleId: number; roleName: string; accessType: string }>;
  employees: { data: any[] } | any[]; // Support both structures for backward compatibility
  FieldUsers: any[];
  COEUsers: any[];
  SiteEvaluators: any[]; // Users for Site Evaluator Name filter
  loading: boolean;
  error: string | null;
  locationRole?: string; // Add locationRole to state
  locationLive?: string; // Add locationLive to state
  lastPeriodWeekFetchDate?: string; // Add field to store the date when period-week was last fetched
  // New fields for location-with-access API
  userInfo?: LocationWithAccessResponse['data']['userInfo'];
  moduleAccess?: LocationWithAccessResponse['data']['moduleAccess'];
  locationSummary?: LocationWithAccessResponse['data']['summary'];
  // Limited access mode - user has no role/location record, redirect to ticketing portal
  noAccessMode: boolean;
  // Basic access user - user found in user_basic_access table, show ticketing portal only
  basicAccessUser?: BasicAccessUser | null;
}

const initialState: AppDataState = {
  locations: [],
  selectedLocation: null,
  periodWeek: { message: "", data: { period: 0, week: 0, year: 0 } },
  roles: [],
  userManagementRoles: [],
  employees: { data: [] }, // Initialize with proper structure
  FieldUsers: [],
  COEUsers: [],
  SiteEvaluators: [],
  loading: true,
  error: null,
  locationRole: undefined, // Add initial value
  locationLive: undefined, // Add initial value
  lastPeriodWeekFetchDate: undefined, // Add initial value
  // New initial values for location-with-access API
  userInfo: undefined,
  moduleAccess: undefined,
  locationSummary: undefined,
  noAccessMode: false,
  basicAccessUser: null,
};

const appDataSlice = createSlice({
  name: "appData",
  initialState,
  reducers: {
    setLocations: (state, action: PayloadAction<any[]>) => {
      console.log("setLocations action payload:", action.payload);
      state.locations = action.payload;
      // Automatically set selectedLocation if there's only one location
      if (action.payload?.length === 1) {
        state.selectedLocation = action.payload[0];
      }
      // Set locationRole if available
      if (action.payload?.length > 0 && action.payload[0].role) {
        state.locationRole = action.payload[0].role;
      }
      // Set locationLive if available
      if (action.payload?.length > 0) {
        const hasLive = action.payload.some((loc: any) => loc.isLive === "Yes");
        state.locationLive = hasLive ? "yes" : "no";
      }
    },
    setLocationWithAccess: (state, action: PayloadAction<LocationWithAccessResponse>) => {
      console.log("setLocationWithAccess action payload:", action.payload);
      const { userInfo, locations, moduleAccess, summary } = action.payload.data;
      
      // Store the new data structure
      state.userInfo = userInfo;
      state.moduleAccess = moduleAccess;
      state.locationSummary = summary;
      
      // Update locations array to maintain backward compatibility
      state.locations = locations;

      // Refresh selectedLocation with latest full data if it already exists
      if (state.selectedLocation?.id) {
        const freshLoc = locations.find(l => l.id === state.selectedLocation!.id);
        if (freshLoc) {
          state.selectedLocation = {
            ...state.selectedLocation,
            ...freshLoc,
            displayText: freshLoc.name || state.selectedLocation.displayText,
          };
        }
      }

      // Automatically set selectedLocation if there's only one location
      if (locations?.length === 1) {
        state.selectedLocation = {
          ...locations[0],
          displayText: locations[0].name, // Ensure displayText is set from the API's name field
          locationType: locations[0].locationType || null, // Preserve locationType if available
          organizationType: locations[0].organizationType || null, // Preserve organizationType if available
          is3pl: locations[0].is3pl != null ? locations[0].is3pl : null, // Preserve is3pl if available
          status: locations[0].status || null, // Preserve status if available
          executionSystem: locations[0].executionSystem || null, // Preserve executionSystem if available
        };
      }
      
      // Set locationRole from userInfo
      if (userInfo?.roleName) {
        state.locationRole = userInfo.roleName;
      }
      
      // Set locationLive from userInfo
      if (userInfo?.isLive) {
        state.locationLive = userInfo.isLive === "Yes" ? "yes" : "no";
      }
    },
        setPeriodWeek: (state, action: PayloadAction<PeriodWeekData>) => {
      state.periodWeek = action.payload;
    },
    setRoles: (state, action: PayloadAction<any[]>) => {
      state.roles = action.payload;
    },
    setUserManagementRoles: (state, action: PayloadAction<Array<{ roleId: number; roleName: string; accessType: string }>>) => {
      state.userManagementRoles = action.payload;
    },
    setEmployees: (state, action: PayloadAction<{ data: any[] } | any[]>) => {
      // Handle both structures: new {data: [...]} or legacy [...]
      if (Array.isArray(action.payload)) {
        // Legacy format: convert to proper structure
        state.employees = { data: action.payload };
      } else {
        // New format: use as is
        state.employees = action.payload;
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setSelectedLocation: (state, action: PayloadAction<any>) => {
      console.log("setSelectedLocation action payload:", action.payload);
      // Handle array with single item
      const location = Array.isArray(action.payload) && action.payload.length === 1
        ? action.payload[0]
        : action.payload;

      if (location && !Array.isArray(location)) {
        state.selectedLocation = {
          ...location,
          displayText: location.displayText || location.name,
          locationType: location.locationType || null,
          organizationType: location.organizationType || null,
          is3pl: location.is3pl != null ? location.is3pl : null,
          status: location.status || null,
          executionSystem: location.executionSystem || null,
        };
      } else {
        state.selectedLocation = location;
      }
    },
    setFieldUsers: (state, action: PayloadAction<any[]>) => {
      state.FieldUsers = action.payload;
    },
    setCOEUsers: (state, action: PayloadAction<any[]>) => {
      state.COEUsers = action.payload;
    },
    setSiteEvaluators: (state, action: PayloadAction<any[]>) => {
      state.SiteEvaluators = action.payload;
    },
    // Memory management actions
    limitEmployees: (state, action: PayloadAction<{ maxItems: number }>) => {
      const { maxItems } = action.payload;
      
      // Handle both data structures for backward compatibility
      if (Array.isArray(state.employees)) {
        if (state.employees.length > maxItems) {
          state.employees = state.employees.slice(0, maxItems);
          console.log(`AppData cleanup: Limited employees array to ${maxItems} items`);
        }
      } else if (state.employees?.data && Array.isArray(state.employees.data)) {
        if (state.employees.data.length > maxItems) {
          state.employees.data = state.employees.data.slice(0, maxItems);
          console.log(`AppData cleanup: Limited employees.data to ${maxItems} items`);
        }
      }
      
      // Limit other user arrays
      if (state.FieldUsers.length > maxItems / 2) {
        state.FieldUsers = state.FieldUsers.slice(0, maxItems / 2);
        console.log(`AppData cleanup: Limited FieldUsers to ${maxItems / 2} items`);
      }
      
      if (state.COEUsers.length > maxItems / 2) {
        state.COEUsers = state.COEUsers.slice(0, maxItems / 2);
        console.log(`AppData cleanup: Limited COEUsers to ${maxItems / 2} items`);
      }
    },
    clearEmployeeData: (state) => {
      // Clear employee-related data while preserving essential app data
      state.employees = { data: [] };
      state.FieldUsers = [];
      state.COEUsers = [];
      state.SiteEvaluators = [];
      console.log('AppData: Employee data cleared for memory optimization');
    },
    setLastPeriodWeekFetchDate: (state, action: PayloadAction<string>) => {
      state.lastPeriodWeekFetchDate = action.payload;
    },
    setNoAccessMode: (state, action: PayloadAction<boolean>) => {
      state.noAccessMode = action.payload;
    },
    setBasicAccessUser: (state, action: PayloadAction<BasicAccessUser | null>) => {
      state.basicAccessUser = action.payload;
      // noAccessMode is set explicitly by AppWrapper's Case 1-3 branches; this reducer must not
      // infer it here, since setBasicAccessUser is also dispatched for full-Navi (Case 4) users.
    },
    updateLocationInState: (state, action: PayloadAction<{ id: string } & Partial<Omit<EnrichedLocation, 'id'>>>) => {
      const { id, ...changes } = action.payload;
      // Update in locations array
      const idx = state.locations.findIndex(l => l.id === id);
      if (idx !== -1) {
        state.locations[idx] = { ...state.locations[idx], ...changes };
      }
      // Sync selectedLocation if it matches
      if (state.selectedLocation?.id === id) {
        state.selectedLocation = {
          ...state.selectedLocation,
          ...changes,
          displayText: changes.name || state.selectedLocation.displayText,
        };
      }
    },
    resetAppDataState: (state) => {
      // Complete reset except for essential location and role data
      const preservedLocation = state.selectedLocation;
      const preservedLocationRole = state.locationRole;
      const preservedLocationLive = state.locationLive;
      const preservedLocations = state.locations;
      const preservedRoles = state.roles;
      
      Object.assign(state, initialState);
      
      // Restore essential data
      state.selectedLocation = preservedLocation;
      state.locationRole = preservedLocationRole;
      state.locationLive = preservedLocationLive;
      state.locations = preservedLocations;
      state.roles = preservedRoles;
      
      console.log('AppData: State reset with essential data preserved');
    },
  },
});

export const {
  setLocations,
  setLocationWithAccess,
  setPeriodWeek,
  setRoles,
  setUserManagementRoles,
  setEmployees,
  setLoading,
  setError,
  setSelectedLocation,
  setFieldUsers,
  setCOEUsers,
  setSiteEvaluators,
  setLastPeriodWeekFetchDate,
  setNoAccessMode,
  setBasicAccessUser,
  // Memory management actions
  limitEmployees,
  clearEmployeeData,
  resetAppDataState,
  updateLocationInState,
} = appDataSlice.actions;

export default appDataSlice.reducer;

